import { UserType } from "@/constants";
import { client } from "@/hera-api/client/client.gen";
import { downloadFileFromResponse } from "@/lib/utils";

export type InvoicePdfPortal = "registrant" | "operator";

/** Invoice PDFs exist only on registrant and operator APIs (not government). */
export function canDownloadInvoicePdf(userType: UserType): boolean {
  return userType === UserType.REGISTRANT || userType === UserType.OPERATOR;
}

export function invoicePdfPortalForUserType(
  userType: UserType,
): InvoicePdfPortal {
  return userType === UserType.REGISTRANT ? "registrant" : "operator";
}

async function errorMessageFromFailedResponse(
  response: Response,
): Promise<string> {
  const text = await response.text();
  let detailMsg = text;
  try {
    const parsed = JSON.parse(text) as { detail?: string | string[] };
    if (typeof parsed.detail === "string") {
      detailMsg = parsed.detail;
    } else if (Array.isArray(parsed.detail)) {
      detailMsg = parsed.detail.join(", ");
    }
  } catch {
    // use raw text
  }
  return detailMsg || response.statusText;
}

/**
 * Fetches the server-generated invoice PDF (global invoice template) and triggers a browser download.
 */
export async function downloadInvoicePdfFromApi(
  manualTransactionId: string,
  portal: InvoicePdfPortal,
): Promise<void> {
  const baseUrl = (client.getConfig().baseUrl || "").replace(/\/$/, "");
  const auth = client.getConfig().auth;
  const url =
    portal === "registrant"
      ? `${baseUrl}/registrant/invoices/${manualTransactionId}/pdf`
      : `${baseUrl}/operator/manual-invoice-payments/${manualTransactionId}/pdf`;
  const doFetch = client.getConfig().fetch ?? globalThis.fetch.bind(globalThis);
  const response = await doFetch(
    new Request(url, {
      headers: { Authorization: `Bearer ${auth}` },
    }),
  );
  if (!response.ok) {
    throw new Error(await errorMessageFromFailedResponse(response));
  }
  const blob = await response.blob();
  downloadFileFromResponse(
    blob,
    response.headers,
    `invoice-${manualTransactionId}.pdf`,
  );
}

export async function downloadInvoicePdfForUserType(
  manualTransactionId: string,
  userType: UserType,
): Promise<void> {
  return downloadInvoicePdfFromApi(
    manualTransactionId,
    invoicePdfPortalForUserType(userType),
  );
}
